import type { DirectoryRegion, DirectorySpecialty } from "@/data/directory-pages";
import { breadcrumbLd, jsonLdScript, SITE_URL } from "@/lib/json-ld";
import type { ProviderRow } from "@/lib/public-content.types";

export type DirectoryPageData = {
  kind: "contractor" | "agent";
  region?: DirectoryRegion;
  specialty?: DirectorySpecialty;
  providers: ProviderRow[];
  serviceAreaProviders?: ProviderRow[];
  unavailable: boolean;
};

export function directoryPageInfo({
  kind,
  region,
  specialty,
}: Pick<DirectoryPageData, "kind" | "region" | "specialty">) {
  const isAgent = kind === "agent";
  const hubPath = isAgent ? "/makler" : "/auftragnehmer";
  const hubName = isAgent ? "Maklerverzeichnis" : "Handwerkerverzeichnis";
  const path = region
    ? `${isAgent ? "/makler" : "/handwerkerverzeichnis"}/${region.slug}`
    : specialty
      ? `${isAgent ? "/makler/leistungen" : "/handwerkerverzeichnis/gewerk"}/${specialty.slug}`
      : hubPath;
  const heading = region
    ? `${isAgent ? "Immobilienmakler" : "Handwerkerverzeichnis"} ${region.name}`
    : specialty
      ? `${specialty.name}: ${isAgent ? "Makler" : "Firmen"} in der Schweiz finden`
      : `${hubName} Schweiz`;
  const title = region
    ? isAgent
      ? `Immobilienmakler ${region.name}: Makler finden | Begner`
      : `Handwerkerverzeichnis ${region.name}: Wohnungsräumung | Begner`
    : specialty
      ? `${specialty.name}: ${isAgent ? "Makler" : "Firmen"} vergleichen | Begner`
      : `${hubName} Schweiz: ${isAgent ? "Immobilienmakler" : "Handwerker"} finden | Begner`;
  const description = region
    ? isAgent
      ? `Immobilienmakler im Kanton ${region.name} finden. Veröffentlichte Profile, Leistungen und Kontaktdaten vergleichen und Anbieter direkt kontaktieren.`
      : `Handwerker und Reinigungsfirmen im Kanton ${region.name}: Wohnungsräumung, Gebäudereinigung und Hauswartung. Firmenprofile und Leistungen vergleichen.`
    : specialty
      ? `${isAgent ? "Immobilienmakler" : "Fachbetriebe"} für ${specialty.name} in der Schweiz vergleichen. Anbieterprofile, Auswahlhilfe und direkter Kontakt im Begner-${hubName}.`
      : isAgent
        ? "Immobilienmakler in der Schweiz finden: Profile, Regionen und Leistungen vergleichen. Makler für Hausverkauf, Wohnungsverkauf und Bewertung direkt kontaktieren."
        : "Handwerkerverzeichnis Schweiz: Fachbetriebe nach Kanton und Gewerk finden. Veröffentlichte Firmenprofile, Leistungen und Kontaktdaten vergleichen.";
  return { hubPath, hubName, path, heading, title, description };
}

export function directoryHead(data: DirectoryPageData | undefined) {
  if (!data)
    return {
      meta: [
        { title: "Verzeichnis nicht gefunden | Begner" },
        { name: "robots", content: "noindex" },
      ],
    };
  const info = directoryPageInfo(data);
  const listedProviders = [...data.providers, ...(data.serviceAreaProviders ?? [])];
  const description =
    data.region && data.serviceAreaProviders?.length
      ? `${data.kind === "agent" ? "Immobilienmakler" : "Handwerker"} für ${data.region.name} finden: Profile nach Standort und angegebenem Tätigkeitsgebiet vergleichen. Überregionale Anbieter separat ansehen.`
      : info.description;
  const crumbs = [
    { name: "Startseite", path: "/" },
    { name: info.hubName, path: info.hubPath },
  ];
  if (data.region || data.specialty)
    crumbs.push({ name: data.region?.name ?? data.specialty!.name, path: info.path });
  return {
    meta: [
      { title: info.title },
      { name: "description", content: description },
      { property: "og:title", content: info.title },
      { property: "og:description", content: description },
      { property: "og:url", content: `${SITE_URL}${info.path}` },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: info.title },
      { name: "twitter:description", content: description },
    ],
    scripts: [
      jsonLdScript(breadcrumbLd(crumbs)),
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: info.heading,
        description,
        url: `${SITE_URL}${info.path}`,
        inLanguage: "de-CH",
        ...(data.region
          ? { spatialCoverage: { "@type": "AdministrativeArea", name: data.region.name } }
          : {}),
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: listedProviders.length,
          itemListElement: listedProviders.map((provider, index) => ({
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "Organization",
              name: provider.name,
              url: `${SITE_URL}${info.path}#anbieter-${provider.id}`,
            },
          })),
        },
      }),
    ],
  };
}
